import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { Stack } from '@mui/material';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ServiceAgreementDto, useProductCenterApi } from 'api';
import { unitServiceAgreementDataQuery } from 'api/queries';
import { AreaName } from 'domain/constants';
import { SectionTitle } from 'library/components/controls/layout/SectionTitle';
import { useDialogState } from 'library/hooks/useDialogState';
import { OperationType } from 'library/models/enums/OperationType';
import AuthorizeComponent from 'main/auth/AuthorizeComponent';
import { checkUserAccess } from 'main/auth/checkUserAccess';
import { reactPlugin } from 'main/azureInsights/config';
import {
	ServiceAgreementForm,
	ServiceAgreementViewSection,
} from 'modules/ServiceAgreement';
import { useResolvedUnitIds } from 'modules/Shared/Components/Hooks/useNumberParam';
import {
	ServiceAgreementFileUploader,
	ServiceAgreementFilesTable,
	ServiceAgreementTransferFiles,
} from 'modules/UnitOverview/ServiceAgreement';

const ServiceAgreementsSectionPage = (): React.JSX.Element => {
	const { t } = useTranslation(['common']);
	const unitIds = useResolvedUnitIds();

	const { open: isEdit, handleClose, handleOpen } = useDialogState(false);
	const {
		open: isAdd,
		handleClose: handleCloseAdd,
		handleOpen: handleOpenAdd,
	} = useDialogState(false);

	const [selectedAgreement, setSelectedAgreement] =
		useState<ServiceAgreementDto | null>(null);

	const { api } = useProductCenterApi();

	const serviceAgreementQuery = useSuspenseQuery(
		unitServiceAgreementDataQuery(api, unitIds.id)
	);

	const queryClient = useQueryClient();
	const canUpdateServiceAgreementFiles = checkUserAccess(queryClient, {
		areaName: AreaName.UnitConfiguration_EditServiceAgreement,
		permission: OperationType.Write,
	});
	const canAdminServiceAgreementFiles = checkUserAccess(queryClient, {
		areaName: AreaName.UnitConfiguration_ServiceAgreementAdmin,
		permission: OperationType.Write,
	});

	if (isAdd) {
		return (
			<AuthorizeComponent
				areaName={AreaName.UnitConfiguration_EditServiceAgreement}
				permission={OperationType.Create}>
				<ServiceAgreementForm
					serviceAgreement={null}
					unitId={unitIds.id}
					onBack={handleCloseAdd}
				/>
			</AuthorizeComponent>
		);
	}

	if (isEdit) {
		return (
			<AuthorizeComponent
				areaName={AreaName.UnitConfiguration_EditServiceAgreement}
				permission={OperationType.Write}>
				<ServiceAgreementForm
					serviceAgreement={selectedAgreement}
					unitId={unitIds.id}
					onBack={() => {
						setSelectedAgreement(null);
						handleClose();
					}}
				/>
			</AuthorizeComponent>
		);
	}

	return (
		<>
			<ServiceAgreementViewSection
				data={serviceAgreementQuery.data || []}
				onEdit={(agreement) => {
					setSelectedAgreement(agreement);
					handleOpen();
				}}
				onAdd={handleOpenAdd}
			/>
			<SectionTitle
				title={t('common:files-word')}
				titleVariant="h6"
				sx={{ mt: 4 }}>
				{(canAdminServiceAgreementFiles ||
					canUpdateServiceAgreementFiles) && (
					<Stack direction="row" spacing={1}>
						<ServiceAgreementTransferFiles unitId={unitIds.id} />
						<ServiceAgreementFileUploader />
					</Stack>
				)}
			</SectionTitle>
			<ServiceAgreementFilesTable
				canWrite={
					canAdminServiceAgreementFiles ||
					canUpdateServiceAgreementFiles
				}
				canAdmin={canAdminServiceAgreementFiles}
				unitId={unitIds.id}
			/>
		</>
	);
};

export default withAITracking(
	reactPlugin,
	ServiceAgreementsSectionPage,
	'UnitOverviewServiceAgreementsSectionPage',
	'application-insight-container'
);
